import type { SkinParticleTexture } from '@kenocyber/shared';
import Phaser from 'phaser';

/**
 * The textures a skin's effects are drawn from (ADR 0041).
 *
 * ## Original, local, and made once
 *
 * Every shape here is drawn by this file with `Graphics.generateTexture`,
 * cached in Phaser's texture manager for the life of the tab, and shared by
 * every ship that needs it. Nothing is downloaded, nothing comes from a CDN,
 * nothing is a shader, and nothing is generated per frame, per ship or per
 * particle.
 *
 * ## What the references contributed, and what they did not
 *
 * The `21st.dev` studies were read as VISUAL LANGUAGE and nothing else: a
 * narrow near-white core, a saturated halo around it, a wide transparent
 * falloff, and incomplete arcs rather than closed rings. That grammar is what
 * the seal below follows.
 *
 * The formulas did not come with it. There is no ported shader, no copied
 * iteration count, no reproduced spiral and no imitation of the original
 * animation - the seal is three arc families whose radii, gaps and thicknesses
 * are chosen here, drawn once into a small square texture, and rotated by the
 * renderer at the slow rate the descriptor asks for.
 */

interface TextureScene {
  readonly textures: Phaser.Textures.TextureManager;
  make: Phaser.GameObjects.GameObjectCreator;
}

/* -------------------------------------------------------------------------- */
/*  Particle shapes                                                           */
/* -------------------------------------------------------------------------- */

/**
 * The four shapes a descriptor may name.
 *
 * `projectile-streak` is deliberately NOT redrawn here: the arena already ships
 * one and a second lozenge that looked almost the same would be two textures
 * doing one job. The renderer resolves it through `ensureStreakTexture`.
 */
export const SKIN_POINT_TEXTURE = 'skin-fx-point';
export const SKIN_CROSS_TEXTURE = 'skin-fx-cross';
export const SKIN_LINE_TEXTURE = 'skin-fx-line';

/** A round, soft dot: glow, embers, solar dust. */
function drawPoint(scene: TextureScene): void {
  const size = 16;
  const centre = size / 2;
  const graphics = scene.make.graphics({ x: 0, y: 0 }, false);
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const distance = Math.hypot(x + 0.5 - centre, y + 0.5 - centre) / centre;
      if (distance >= 1) {
        continue;
      }
      /*
       * Core, halo, falloff - the three-part grammar, in one dot. The fourth
       * power is what keeps the bright part SMALL: a linear falloff reads as a
       * flat disc and six of them over a hull is a white smear.
       */
      const falloff = (1 - distance) ** 4;
      graphics.fillStyle(0xffffff, Math.min(1, 0.08 + 0.92 * falloff));
      graphics.fillRect(x, y, 1, 1);
    }
  }
  graphics.generateTexture(SKIN_POINT_TEXTURE, size, size);
  graphics.destroy();
}

/** A four-pointed star: a glint off a jewel or a polished edge. */
function drawCross(scene: TextureScene): void {
  const size = 16;
  const centre = size / 2;
  const graphics = scene.make.graphics({ x: 0, y: 0 }, false);
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const dx = Math.abs(x + 0.5 - centre) / centre;
      const dy = Math.abs(y + 0.5 - centre) / centre;
      // Two crossed spikes plus a small hot centre.
      const spike =
        Math.max(0, 1 - dx * 4) * Math.max(0, 1 - dy) +
        Math.max(0, 1 - dy * 4) * Math.max(0, 1 - dx);
      const core = Math.max(0, 1 - Math.hypot(dx, dy) * 3) ** 2;
      const alpha = Math.min(1, spike * 0.5 + core);
      if (alpha <= 0.01) {
        continue;
      }
      graphics.fillStyle(0xffffff, alpha);
      graphics.fillRect(x, y, 1, 1);
    }
  }
  graphics.generateTexture(SKIN_CROSS_TEXTURE, size, size);
  graphics.destroy();
}

/** An elongated soft streak: speed, a drawn-out spark. */
function drawLine(scene: TextureScene): void {
  const length = 20;
  const height = 6;
  const graphics = scene.make.graphics({ x: 0, y: 0 }, false);
  for (let i = 0; i < length; i += 1) {
    const t = i / (length - 1);
    // Fullest a third of the way along, so it reads as travelling rather than
    // as a symmetrical lozenge.
    const shape = Math.sin(Math.PI * t ** 0.7);
    const half = 1.5 * shape;
    if (half <= 0.05) {
      continue;
    }
    graphics.fillStyle(0xffffff, 0.2 + 0.7 * shape);
    graphics.fillRect(i, height / 2 - half, 1, half * 2);
  }
  graphics.generateTexture(SKIN_LINE_TEXTURE, length, height);
  graphics.destroy();
}

/**
 * The Phaser texture key for one of the descriptor's four shapes.
 *
 * Created on first use and cached for the tab. A skin cannot introduce a
 * texture of its own - the vocabulary is closed - so this map is total.
 */
export function ensureSkinParticleTexture(
  scene: TextureScene,
  texture: SkinParticleTexture,
  streakKey: string,
): string {
  switch (texture) {
    case 'projectile-streak':
      // The arena's own lozenge, already cached by the projectile art.
      return streakKey;
    case 'star-point':
      if (!scene.textures.exists(SKIN_POINT_TEXTURE)) {
        drawPoint(scene);
      }
      return SKIN_POINT_TEXTURE;
    case 'star-cross':
      if (!scene.textures.exists(SKIN_CROSS_TEXTURE)) {
        drawCross(scene);
      }
      return SKIN_CROSS_TEXTURE;
    case 'star-streak':
      if (!scene.textures.exists(SKIN_LINE_TEXTURE)) {
        drawLine(scene);
      }
      return SKIN_LINE_TEXTURE;
    /*
     * The painted five. Loaded from a file rather than drawn (gate V27A).
     *
     * The key IS the texture key, because the loader registers it under that
     * name before a scene builds a skin. A key that is not loaded yet returns
     * the procedural dot instead of an empty texture: a light that is missing
     * for one frame is a light nobody sees, and a missing texture in Phaser is
     * a green-and-black checkerboard that everybody sees.
     */
    case 'vfx-core-cyan':
    case 'vfx-core-gold':
    case 'vfx-core-prismatic':
    case 'vfx-streak':
    case 'vfx-ring':
      if (scene.textures.exists(texture)) {
        return texture;
      }
      if (!scene.textures.exists(SKIN_POINT_TEXTURE)) {
        drawPoint(scene);
      }
      return SKIN_POINT_TEXTURE;
  }
}

/**
 * The five painted lights, and where the build put them.
 *
 * One list, used by the loader that registers them and by the test that proves
 * every declared texture can be reached. `emit-vfx.ts` writes exactly these
 * names under `vfx/`.
 */
/** Where the build writes them. The same root every other game asset uses. */
export const SKIN_VFX_BASE_URL = '/game-assets/';

export const SKIN_VFX_TEXTURES: readonly { readonly key: string; readonly file: string }[] =
  Object.freeze([
    { key: 'vfx-core-cyan', file: 'vfx/particle-core-cyan.png' },
    { key: 'vfx-core-gold', file: 'vfx/particle-core-gold.png' },
    { key: 'vfx-core-prismatic', file: 'vfx/particle-core-prismatic.png' },
    { key: 'vfx-streak', file: 'vfx/particle-streak-neutral.png' },
    { key: 'vfx-ring', file: 'vfx/ship-energy-ring-neutral.png' },
  ]);

/* -------------------------------------------------------------------------- */
/*  The seal                                                                  */
/* -------------------------------------------------------------------------- */

export const SKIN_SEAL_TEXTURE = 'skin-fx-seal';

/** How many source pixels the seal is drawn into. Scaled down in world space. */
const SEAL_SIZE = 128;

/**
 * A small technological seal: three families of INCOMPLETE arcs.
 *
 * ## Why incomplete, and why this shape
 *
 * A closed ring around anything reads as a boundary - a hitbox, a damage area,
 * a selection. The whole point of this mark is that it sits ON the hull and
 * annotates nothing, so every arc has gaps, the outer family is the thinnest,
 * and the brightest part is a short tick rather than a circle.
 *
 * It is drawn white and TINTED by the renderer from the descriptor's palette,
 * which is what lets one 128px texture serve every skin instead of one per
 * colour set.
 *
 * ## What it is not
 *
 * It is not the reference's ring and it is not its spiral. The radii, the gap
 * pattern, the tick spacing and the three-family construction are chosen here;
 * nothing was ported, and the original's animation - which followed a mouse -
 * has no analogue at all.
 */
function drawSeal(scene: TextureScene): void {
  const graphics = scene.make.graphics({ x: 0, y: 0 }, false);
  const centre = SEAL_SIZE / 2;

  /** One arc family: radius, thickness, how much of the circle is drawn. */
  const families = [
    { radius: 0.94, thickness: 1.2, arcs: 3, span: 0.36, alpha: 0.5 },
    { radius: 0.7, thickness: 2.2, arcs: 2, span: 0.42, alpha: 0.8 },
    { radius: 0.44, thickness: 1.6, arcs: 5, span: 0.1, alpha: 0.95 },
  ];

  for (const family of families) {
    const radius = family.radius * (centre - 2);
    for (let index = 0; index < family.arcs; index += 1) {
      const start = (index / family.arcs) * Math.PI * 2 + index * 0.21;
      const end = start + family.span * Math.PI * 2;
      graphics.lineStyle(family.thickness, 0xffffff, family.alpha);
      graphics.beginPath();
      graphics.arc(centre, centre, radius, start, end);
      graphics.strokePath();
    }
  }

  /*
   * The core: a short bright tick through the middle, which is what makes the
   * mark read as energy rather than as a diagram. Small on purpose - a bright
   * centre the size of the seal would be the "white spread over the ship" the
   * reference review calls a failure.
   */
  graphics.lineStyle(2.4, 0xffffff, 1);
  graphics.beginPath();
  graphics.moveTo(centre, centre - (centre - 2) * 0.22);
  graphics.lineTo(centre, centre + (centre - 2) * 0.22);
  graphics.strokePath();

  graphics.generateTexture(SKIN_SEAL_TEXTURE, SEAL_SIZE, SEAL_SIZE);
  graphics.destroy();
}

export function ensureSkinSealTexture(scene: TextureScene): string {
  if (!scene.textures.exists(SKIN_SEAL_TEXTURE)) {
    drawSeal(scene);
  }
  return SKIN_SEAL_TEXTURE;
}

/** Every texture this module can create. Used by the teardown proof. */
export const SKIN_GENERATED_TEXTURES = [
  SKIN_POINT_TEXTURE,
  SKIN_CROSS_TEXTURE,
  SKIN_LINE_TEXTURE,
  SKIN_SEAL_TEXTURE,
] as const;

/** `#rrggbb` to the integer Phaser tints with. */
export function skinColorValue(color: string): number {
  return Phaser.Display.Color.HexStringToColor(color).color;
}
